// AI assisted development
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  LinearProgress,
  Tooltip,
  Fab,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays } from 'date-fns';
import api from '@/services/api';
import { ReleaseNoteStatus } from '@/types';

const JIRA_BASE_URL = import.meta.env.VITE_JIRA_BASE_URL || 'https://upshop-company.atlassian.net';

const brandTeal = '#006261';

const statusColors: Record<ReleaseNoteStatus, { bg: string; color: string; label: string }> = {
  [ReleaseNoteStatus.Pending]: { bg: '#FF9800', color: '#FFFFFF', label: 'Pending' },
  [ReleaseNoteStatus.Generated]: { bg: '#4CAF50', color: '#FFFFFF', label: 'Generated' },
  [ReleaseNoteStatus.Edited]: { bg: '#9C27B0', color: '#FFFFFF', label: 'Edited' },
  [ReleaseNoteStatus.Published]: { bg: '#2196F3', color: '#FFFFFF', label: 'Published' },
  [ReleaseNoteStatus.Failed]: { bg: '#F44336', color: '#FFFFFF', label: 'Failed' },
  [ReleaseNoteStatus.Excluded]: { bg: '#9E9E9E', color: '#FFFFFF', label: 'Excluded' },
};

const ISSUE_TYPE_OPTIONS = ['Story', 'Bug', 'Customer Defect', 'Task', 'Epic'];

const issueTypeColors: Record<string, { bg: string; color: string }> = {
  'Story': { bg: '#4CAF50', color: '#FFFFFF' },
  'Bug': { bg: '#F44336', color: '#FFFFFF' },
  'Customer Defect': { bg: '#FF5722', color: '#FFFFFF' },
  'Task': { bg: '#2196F3', color: '#FFFFFF' },
  'Epic': { bg: '#9C27B0', color: '#FFFFFF' },
};

type SortField = 'issueKey' | 'summary' | 'productSuite' | 'customer' | 'status' | 'generatedAt' | 'issueType';

function defaultGeneratedAfter(): string {
  return format(subDays(new Date(), 10), 'yyyy-MM-dd');
}

function defaultGeneratedBefore(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasUrlDates = searchParams.has('generatedAfter') || searchParams.has('generatedBefore');

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [productSuiteFilter, setProductSuiteFilter] = useState<string>(() => searchParams.get('productSuite') ?? '');
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') ?? '');
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') ?? '');
  const [issueTypesFilter, setIssueTypesFilter] = useState<string[]>(() => {
    const v = searchParams.get('issueTypes');
    return v ? v.split(',').filter(Boolean) : [];
  });
  const [generatedAfter, setGeneratedAfter] = useState<string | null>(() =>
    searchParams.get('generatedAfter') ?? (hasUrlDates ? null : defaultGeneratedAfter())
  );
  const [generatedBefore, setGeneratedBefore] = useState<string | null>(() =>
    searchParams.get('generatedBefore') ?? (hasUrlDates ? null : defaultGeneratedBefore())
  );
  const [page, setPage] = useState(() => Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1));
  const [pageSize, setPageSize] = useState(() => {
    const n = parseInt(searchParams.get('pageSize') || '50', 10);
    return [25, 50, 100].includes(n) ? n : 50;
  });
  const [sortBy, setSortBy] = useState<SortField>(() => (searchParams.get('sortBy') as SortField) || 'generatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() =>
    searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc'
  );

  const [importOpen, setImportOpen] = useState(false);
  const [importKeys, setImportKeys] = useState('');
  const [importMessage, setImportMessage] = useState<string | null>(null);

  // Sync filters to URL when they change
  useEffect(() => {
    const next = new URLSearchParams();
    if (productSuiteFilter) next.set('productSuite', productSuiteFilter);
    if (statusFilter) next.set('status', statusFilter);
    if (searchTerm) next.set('search', searchTerm);
    if (issueTypesFilter.length) next.set('issueTypes', issueTypesFilter.join(','));
    if (generatedAfter) next.set('generatedAfter', generatedAfter);
    if (generatedBefore) next.set('generatedBefore', generatedBefore);
    if (page > 1) next.set('page', String(page));
    if (pageSize !== 50) next.set('pageSize', String(pageSize));
    if (sortBy !== 'generatedAt') next.set('sortBy', sortBy);
    if (sortDir !== 'desc') next.set('sortDir', sortDir);
    setSearchParams(next, { replace: true });
  }, [productSuiteFilter, statusFilter, searchTerm, issueTypesFilter, generatedAfter, generatedBefore, page, pageSize, sortBy, sortDir, setSearchParams]);

  const resetToFirstPage = () => setPage(1);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['releaseNoteItems', productSuiteFilter, statusFilter, searchTerm, issueTypesFilter, generatedAfter, generatedBefore, page, pageSize, sortBy, sortDir],
    queryFn: () => api.getReleaseNoteItems({
      productSuite: productSuiteFilter || undefined,
      status: statusFilter ? parseInt(statusFilter) : undefined,
      searchTerm: searchTerm || undefined,
      issueTypes: issueTypesFilter.length ? issueTypesFilter : undefined,
      generatedAfter: generatedAfter || undefined,
      generatedBefore: generatedBefore || undefined,
      sortBy,
      sortDir,
      excludePublished: !statusFilter,
      excludeExcluded: !statusFilter || statusFilter !== '5',
    }, page, pageSize),
  });

  const { data: itemStats } = useQuery({
    queryKey: ['releaseNoteItemStats'],
    queryFn: () => api.getReleaseNoteItemStats(),
  });

  const { data: productSuites = [] } = useQuery({
    queryKey: ['productSuites'],
    queryFn: () => api.getProductSuites(),
    select: (d) => (Array.isArray(d) ? d : []),
  });

  const importMutation = useMutation({
    mutationFn: (keys: string[]) => api.importReleaseNoteItems(keys),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['releaseNoteItems'] });
      const errCount = result.errors?.length ?? 0;
      setImportMessage(
        errCount > 0
          ? `Imported ${result.items.length} item(s). ${errCount} error(s): ${result.errors.map((e) => `${e.issueKey}: ${e.error}`).join('; ')}`
          : `Imported ${result.items.length} item(s) successfully.`
      );
      setImportKeys('');
    },
    onError: (err: Error) => {
      setImportMessage(err.message || 'Import failed');
    },
  });

  const items = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(items.map((item) => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleClearFilters = () => {
    setProductSuiteFilter('');
    setStatusFilter('');
    setSearchTerm('');
    setIssueTypesFilter([]);
    setGeneratedAfter(null);
    setGeneratedBefore(null);
    setPage(1);
    setSortBy('generatedAt');
    setSortDir('desc');
  };

  const handleClearDates = () => {
    setGeneratedAfter(null);
    setGeneratedBefore(null);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(field === 'generatedAt' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const handleAddToRelease = () => {
    navigate('/publish', { state: { selectedIds: Array.from(selectedItems) } });
  };

  const dashboardPath = location.pathname + location.search;
  const handleRowClick = (id: string, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('input[type="checkbox"]') || target.closest('a')) {
      return;
    }
    navigate(`/editor/${id}`, { state: { returnTo: dashboardPath } });
  };

  const handleOpenEditor = (itemId: string) => {
    navigate(`/editor/${itemId}`, { state: { returnTo: dashboardPath } });
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusChip = (status: ReleaseNoteStatus) => {
    const config = statusColors[status] || statusColors[ReleaseNoteStatus.Pending];
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 500 }}
      />
    );
  };

  const getIssueTypeChip = (issueType: string) => {
    const config = issueTypeColors[issueType] || { bg: '#E5E7EB', color: '#374151' };
    return (
      <Chip
        label={issueType}
        size="small"
        sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 500 }}
      />
    );
  };

  const handleImport = () => {
    const keys = importKeys
      .split(/[\s,;]+/)
      .map((k) => k.trim())
      .filter(Boolean);
    if (keys.length === 0) {
      setImportMessage('Enter at least one Jira issue key (e.g. UP-12345).');
      return;
    }
    setImportMessage(null);
    importMutation.mutate(keys);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Release Note Items
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => { setImportOpen(true); setImportMessage(null); }}
          >
            Import from Jira
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear Filters">
            <IconButton onClick={handleClearFilters}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#E8F5E9', borderLeft: `4px solid ${brandTeal}` }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Tickets edited (status: Edited)
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, color: brandTeal }}>
          {itemStats?.editedTicketCount ?? '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Count of release note items that have been edited after generation (includes items removed from a published release).
        </Typography>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); resetToFirstPage(); }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Product Suite</InputLabel>
              <Select
                value={productSuiteFilter}
                label="Product Suite"
                onChange={(e: SelectChangeEvent) => { setProductSuiteFilter(e.target.value); resetToFirstPage(); }}
              >
                <MenuItem value="">All</MenuItem>
                {Array.isArray(productSuites) && productSuites.map((suite) => (
                  <MenuItem key={suite} value={suite}>{suite}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => { setStatusFilter(e.target.value); resetToFirstPage(); }}
              >
                <MenuItem value="">All (excl. Published/Excluded)</MenuItem>
                <MenuItem value="0">Pending</MenuItem>
                <MenuItem value="1">Generated</MenuItem>
                <MenuItem value="2">Edited</MenuItem>
                <MenuItem value="3">Published</MenuItem>
                <MenuItem value="4">Failed</MenuItem>
                <MenuItem value="5">Excluded</MenuItem>
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={ISSUE_TYPE_OPTIONS}
              value={issueTypesFilter}
              onChange={(_, newValue) => { setIssueTypesFilter(newValue); resetToFirstPage(); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Issue type"
                  placeholder={issueTypesFilter.length === 0 ? 'All types' : ''}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{ backgroundColor: issueTypeColors[option]?.bg ?? '#E5E7EB', color: issueTypeColors[option]?.color ?? '#374151' }}
                  />
                ))
              }
              sx={{ minWidth: 280, maxWidth: 420 }}
            />
            <DatePicker
              label="Generated from"
              value={generatedAfter ? new Date(generatedAfter + 'T00:00:00') : null}
              onChange={(d) => { setGeneratedAfter(d ? format(d, 'yyyy-MM-dd') : null); resetToFirstPage(); }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <DatePicker
              label="Generated to"
              value={generatedBefore ? new Date(generatedBefore + 'T00:00:00') : null}
              onChange={(d) => { setGeneratedBefore(d ? format(d, 'yyyy-MM-dd') : null); resetToFirstPage(); }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <Button size="small" onClick={() => { handleClearDates(); resetToFirstPage(); }} disabled={!generatedAfter && !generatedBefore}>
              Clear dates
            </Button>
          </Box>
        </LocalizationProvider>
      </Paper>

      <TableContainer component={Paper}>
        {isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedItems.size > 0 && selectedItems.size < items.length}
                  checked={items.length > 0 && selectedItems.size === items.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell sortDirection={sortBy === 'issueKey' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'issueKey'} direction={sortBy === 'issueKey' ? sortDir : 'asc'} onClick={() => handleSort('issueKey')}>
                  Jira Key
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'issueType' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'issueType'} direction={sortBy === 'issueType' ? sortDir : 'asc'} onClick={() => handleSort('issueType')}>
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'summary' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'summary'} direction={sortBy === 'summary' ? sortDir : 'asc'} onClick={() => handleSort('summary')}>
                  Summary
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'productSuite' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'productSuite'} direction={sortBy === 'productSuite' ? sortDir : 'asc'} onClick={() => handleSort('productSuite')}>
                  Product Suite
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'customer' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'customer'} direction={sortBy === 'customer' ? sortDir : 'asc'} onClick={() => handleSort('customer')}>
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'status' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'status'} direction={sortBy === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'generatedAt' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'generatedAt'} direction={sortBy === 'generatedAt' ? sortDir : 'desc'} onClick={() => handleSort('generatedAt')}>
                  Generated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.id}
                hover
                onClick={(e) => handleRowClick(item.id, e)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedItems.has(item.id)}
                    onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <a
                    href={item.jiraUrl || `${JIRA_BASE_URL}/browse/${item.issueKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      color: brandTeal,
                      textDecoration: 'none',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {item.issueKey}
                    <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                  </a>
                </TableCell>
                <TableCell>{item.issueType && getIssueTypeChip(item.issueType)}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.summary}
                </TableCell>
                <TableCell>
                  {item.productSuite && (
                    <Chip
                      label={item.productSuite}
                      size="small"
                      sx={{ backgroundColor: '#E0F2F1', color: brandTeal, fontWeight: 500 }}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {item.customer ?? '—'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {getStatusChip(item.status)}
                    {item.status === ReleaseNoteStatus.Published && (item.publishedAt || item.publishedVersion) && (
                      <Typography variant="caption" color="text.secondary">
                        {item.publishedAt && formatDate(item.publishedAt)}
                        {item.publishedAt && item.publishedVersion && ' · '}
                        {item.publishedVersion && `v${item.publishedVersion}`}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {item.generatedAt ? formatDate(item.generatedAt) : '-'}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenEditor(item.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No items found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mt: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {items.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + items.length} of {totalCount} items
          {!statusFilter && ' (Published and Excluded hidden by default)'}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Per page</InputLabel>
            <Select
              value={String(pageSize)}
              label="Per page"
              onChange={(e: SelectChangeEvent) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            >
              <MenuItem value="25">25</MenuItem>
              <MenuItem value="50">50</MenuItem>
              <MenuItem value="100">100</MenuItem>
            </Select>
          </FormControl>
          <Pagination
            count={totalPages}
            page={Math.min(page, totalPages)}
            onChange={(_, p) => setPage(p)}
            color="primary"
            size="small"
          />
        </Stack>
      </Stack>

      {selectedItems.size > 0 && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleAddToRelease}
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add to Release ({selectedItems.size})
        </Fab>
      )}

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import from Jira</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter one or more Jira issue keys (comma or newline separated) to pull into Release Notes Manager when they were not created by the webhook.
          </Typography>
          <TextField
            label="Issue keys"
            placeholder="UP-38274, UP-42166"
            multiline
            minRows={3}
            fullWidth
            value={importKeys}
            onChange={(e) => setImportKeys(e.target.value)}
          />
          {importMessage && (
            <Alert severity={importMessage.toLowerCase().includes('error') || importMessage.toLowerCase().includes('fail') ? 'warning' : 'success'} sx={{ mt: 2 }}>
              {importMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleImport} disabled={importMutation.isPending}>
            {importMutation.isPending ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
